import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Loader2, Copy, Check } from "lucide-react";
import { streamPost } from "@/lib/stream";

const SENIORITY = ["Entry level", "Mid level", "Senior", "Executive"];
const FOCUS_AREAS = ["Fair housing", "Leadership", "Conflict handling", "Ops rigor", "Resident experience", "Safety"];

const NOVA_AVATAR =
  "https://images.unsplash.com/photo-1728577740843-5f29c7586afe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjb2xvcmZ1bCUyMGF2YXRhciUyMGNoYXJhY3RlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc5MDQ1MDE4Mnww&ixlib=rb-4.1.0&q=85&w=200";

export default function InterviewStudio() {
  const [role, setRole] = useState("");
  const [seniority, setSeniority] = useState(SENIORITY[1]);
  const [focus, setFocus] = useState(["Fair housing"]);
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const toggleFocus = (f) =>
    setFocus((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    setOutput("");
    try {
      await streamPost(
        "/interview",
        { role, seniority, focus: focus.join(", ") },
        (delta) => setOutput((o) => o + delta)
      );
    } catch {
      setOutput("Interview kit failed to generate — please try again.");
    }
    setBusy(false);
  };

  const copy = async () => {
    await navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const inputCls =
    "w-full bg-white rounded-2xl border-2 border-slate-900/10 px-4 py-3 text-sm font-medium outline-none focus:border-[#10B981] transition-colors placeholder:text-slate-400";

  return (
    <section id="interview-studio" className="py-24 sm:py-32 bg-white border-y-2 border-slate-900/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#10B981]">AI Tool №3 · Gemini</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mt-3">
            Nova builds the interview. You build the team.
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed mt-4">
            Tell Gemini the role and what matters — get a complete interview kit with questions,
            what to listen for, and a scoring rubric, streamed live.
          </p>
        </motion.div>

        <motion.div
          data-testid="gemini-studio-container"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="grid lg:grid-cols-[0.9fr_1.1fr] gap-6 mt-12"
        >
          <div className="rounded-3xl border-2 border-slate-900/5 bg-[#FBFBF9] p-7 shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)] space-y-4">
            <div className="flex items-center gap-3 pb-2">
              <img src={NOVA_AVATAR} alt="Nova, the Gemini interview architect" className="w-11 h-11 rounded-2xl object-cover border-2 border-slate-900/5" />
              <div>
                <div className="font-display font-bold text-sm">Nova · Interview Architect</div>
                <div className="text-xs font-bold text-[#10B981]">Gemini 3 Flash engine</div>
              </div>
            </div>
            <input data-testid="gemini-role-input" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role, e.g. Community Manager" className={inputCls} />
            <select data-testid="gemini-seniority-select" value={seniority} onChange={(e) => setSeniority(e.target.value)} className={inputCls}>
              {SENIORITY.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Focus areas</div>
              <div className="flex flex-wrap gap-2">
                {FOCUS_AREAS.map((f, i) => (
                  <button
                    key={f}
                    data-testid={`gemini-focus-chip-${i}`}
                    type="button"
                    onClick={() => toggleFocus(f)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold border-2 transition-colors duration-200 ${
                      focus.includes(f) ? "bg-[#10B981] text-white border-[#10B981]" : "border-slate-900/10 text-slate-600 hover:border-[#10B981] hover:text-[#10B981]"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <button
              data-testid="gemini-generate-button"
              onClick={generate}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#10B981] text-white font-bold text-sm hover:bg-[#0F172A] transition-colors duration-300 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Gemini is building…" : "Build interview kit with Gemini"}
            </button>
          </div>

          <div className="rounded-3xl bg-[#0F172A] p-7 shadow-[0_30px_80px_-25px_rgba(15,23,42,0.4)] flex flex-col min-h-[26rem]">
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5722]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
                <span className="ml-3 text-xs font-bold tracking-widest uppercase text-slate-400">Live kit preview</span>
              </div>
              <button
                onClick={copy}
                disabled={!output}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 text-xs font-bold text-white hover:bg-[#10B981] transition-colors disabled:opacity-40"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div data-testid="gemini-output-preview" className="doc-preview doc-scroll flex-1 overflow-y-auto pt-5 text-sm leading-relaxed text-slate-300">
              {output || (
                <span className="text-slate-500">
                  Your interview kit will stream here the moment Gemini starts building…
                </span>
              )}
              {busy && <span className="inline-block w-2 h-4 bg-[#10B981] animate-pulse ml-0.5 align-middle" />}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
